import keyBy from 'lodash/keyBy'
import {Cmd, Loop, LoopReducer, loop} from 'redux-loop'

import config from '../config'

import {Actions} from '../actions'
import * as authActions from '../actions/auth'
import * as orderActions from '../actions/order'

import {HttpStatusError} from '../../../commons/src/libs/http-json'
import {cancelablePromise} from '../../../commons/src/libs/promise'
import {
  getDateXDaysBefore,
  getEndOfDay,
  getStartOfDay,
  getTodaysDate,
} from '../../../commons/src/services/date'
import * as localStorage from '../../../commons/src/services/local-storage'
import {captureException, captureIgnoredException} from '../../../commons/src/services/logging'
import {transformInternalOrder} from '../libs/order'
import {StringMatchingFilter} from '../libs/table-row-filter'
import * as internalApi from '../services/api/internal'
import * as partnerApi from '../services/api/partner'

import {DateRange} from '../../../commons/src/types'

import {Order, OrderNumber, OrderStatusType} from '../order-types'

export type OrderState = {
  orders: Record<OrderNumber, Order>
  isLoading: boolean
  failed: boolean
  statusFilter: StringMatchingFilter<{
    status: OrderStatusType
  }>
  creationDateRange: DateRange
}

export const getInitialStatusFilter = () =>
  new StringMatchingFilter({
    key: 'status',
    filterValues:
      localStorage.getItem(config.localStorageKeys.defaultStatusFilters) ||
      config.defaultStatusFilters,
  })

export const initialCreationDateRange = {
  from: getDateXDaysBefore(getTodaysDate(), config.dateRangeConfig.creationDate.defaultFromDays),
  to: getDateXDaysBefore(getTodaysDate(), config.dateRangeConfig.creationDate.defaultToDays),
}

const initialState: OrderState = {
  orders: {},
  isLoading: false,
  failed: false,
  statusFilter: getInitialStatusFilter(),
  creationDateRange: initialCreationDateRange,
}

type OrderReducer = OrderState | Loop<OrderState>

let currentLoadOrdersCancel: (() => void) | undefined

const loadOrders = (state: OrderState, {payload}: orderActions.LoadOrdersAction): OrderReducer =>
  loop(
    {...state, isLoading: true, failed: false},
    Cmd.run(
      getState => {
        // Cancel previous request if still ongoing
        if (currentLoadOrdersCancel) {
          currentLoadOrdersCancel()
        }

        const filterValues = state.statusFilter.serialize().filterValues as OrderStatusType[]
        const dateRange = {
          from: getStartOfDay(state.creationDateRange.from),
          to: getEndOfDay(state.creationDateRange.to),
        }
        const currentState = getState()
        const providerId = currentState.core.provider.id

        if (currentState.auth.isAdminMode) {
          // Use internal API
          const {promise: promiseLoadOrders, cancel: cancelLoadOrders} = cancelablePromise(
            internalApi.getOrders(providerId, dateRange, filterValues)
          )
          currentLoadOrdersCancel = cancelLoadOrders

          return promiseLoadOrders.then(orders =>
            orders.map(order => transformInternalOrder(order, providerId))
          )
        }

        // Use Partner API
        const {promise: promiseLoadOrders, cancel: cancelLoadOrders} = cancelablePromise(
          partnerApi.getOrders(dateRange, filterValues)
        )
        currentLoadOrdersCancel = cancelLoadOrders

        return promiseLoadOrders
      },
      {
        successActionCreator: (orders: Order[]) => {
          currentLoadOrdersCancel = undefined
          return orderActions.ordersReceived(orders)
        },
        failActionCreator: (error: Error) => {
          if (error && error.name === 'PromiseCancelledError') {
            return Cmd.none
          }
          if (error && error instanceof HttpStatusError && error.response.status === 401) {
            captureIgnoredException(error)
            return authActions.signOut()
          }
          // No error handling here except tracking. If orders could not be loaded we inform the partner about it on the orders page.
          captureException(error)
          return orderActions.loadOrdersFailed()
        },
        args: [Cmd.getState],
      }
    )
  )

const loadOrdersFailed = (
  state: OrderState,
  {payload}: orderActions.LoadOrdersFailedAction
): OrderReducer => ({
  ...state,
  orders: {},
  isLoading: false,
  failed: true,
})

const ordersReceived = (
  state: OrderState,
  {payload}: orderActions.OrdersReceivedAction
): OrderReducer => ({
  ...state,
  orders: keyBy(payload, order => order.orderId),
  isLoading: false,
  failed: false,
})

const updateOrder = (state: OrderState, {payload}: orderActions.UpdateOrderAction) => ({
  ...state,
  orders: keyBy([...Object.values(state.orders), payload], (order: Order) => order.orderId),
})

const updateStatusFilter = (state: OrderState, {payload}: orderActions.UpdateStatusFilterAction) =>
  loop(
    {
      ...state,
      statusFilter: payload,
    },
    Cmd.action(orderActions.loadOrders())
  )

const updateCreationDateRange = (
  state: OrderState,
  {payload}: orderActions.UpdateCreationDateRangeAction
) =>
  loop(
    {
      ...state,
      creationDateRange: payload,
    },
    Cmd.action(orderActions.loadOrders())
  )

const signInDone = (state: OrderState, {payload}: authActions.SignInDoneAction): OrderReducer =>
  loop(
    state,
    Cmd.run(
      dispatch => {
        // We are not waiting for the orders response to finish the sign in
        dispatch(orderActions.loadOrders())
      },
      {
        args: [Cmd.dispatch],
      }
    )
  )

const signOut = (state: OrderState): OrderReducer => initialState

const reducer: LoopReducer<OrderState, Actions> = (state = initialState, action) => {
  switch (action.type) {
    case 'ORDER.LOAD_ORDERS':
      return loadOrders(state, action)
    case 'ORDER.ORDERS_RECEIVED':
      return ordersReceived(state, action)
    case 'ORDER.LOAD_ORDERS_FAILED':
      return loadOrdersFailed(state, action)
    case 'ORDER.UPDATE_ORDER':
      return updateOrder(state, action)
    case 'ORDER.UPDATE_STATUSFILTER':
      return updateStatusFilter(state, action)
    case 'ORDER.UPDATE_CREATION_DATE_RANGE':
      return updateCreationDateRange(state, action)
    case 'AUTH.SIGN_IN_DONE':
      return signInDone(state, action)
    case 'AUTH.SIGN_OUT':
      return signOut(state)
    default:
      return state
  }
}

export default reducer
